<template>
  <div class="component">
    <b-breadcrumb :items="items"></b-breadcrumb>
    <div class="container-fluid">
      <div class="row align-items-center my-3" style="margin-left: 20px; margin-right: 20px;">
        <b-col sm="7" class="d-flex align-items-center">
          <label for="filterInput" class="mr-2 mb-0">Buscar</label>
          <b-input-group>
          <multiselect
            id="filterInput"
            v-model="selectedFilter"
            :options="filterOptions"
            :searchable="true"
            :internal-search="false"
            :close-on-select="true"
            :show-labels="false"
            placeholder="Filtre por Nome, CNPJ ou Empresa"
            track-by="value"
            label="text"
            class="flex-grow-1"
            @search-change="onSearchChange"
          />
          </b-input-group>
        </b-col>
        <b-col sm="5" class="d-flex justify-content-end align-items-center">
          <label for="companiesPerPage" class="mr-2 mb-0">Itens por página</label>
          <select
            id="companiesPerPage"
            v-model.number="perPage"
            class="form-control"
            style="width: auto"
            @change="onPerPageChange(perPage)"
          >
            <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }}</option>
          </select>
        </b-col>
      </div>
        <b-table
          :fields="fields"
          :items="paginatedCompanies"
          @filtered="onFiltered"
        >
        <template v-slot:cell(id)="data">
          <b-button class="trash mr-4" @click="openModal('confirm-modal', data.value)">
            <font-awesome-icon icon="trash" color="red" />
          </b-button>
          <router-link :to="{ name: 'Companies_New', params: { id: data.value }}">
            <b-button class="pencil">
              <font-awesome-icon :icon="role == `admin` ? `pencil-alt`:`external-link-alt`" />
            </b-button>
          </router-link>
        </template>
      </b-table>
      <b-pagination
        class="float-right"
        v-model="currentPage"
        :total-rows="rows"
        :per-page="perPage"
        aria-controls="my-table"
      ></b-pagination>
      <b-modal
        ref="confirm-modal"
        title="Excluir Unidade"
        modal-ok="Sim"
        modal-cancel="Não"
        @ok="removeCompany()"
      >
        Deseja mesmo excluir essa unidade?
        <template v-slot:modal-cancel>Cancelar</template>
        <template v-slot:modal-ok>Confirmar</template>
      </b-modal>
      <br />
      <br />
      <b-alert
        v-model="showAlertDeleteCompany"
        variant="danger"
        @dismissed="dismissCountDown=0"
        @dismiss-count-down="countDownChanged"
        dismissible
      >Você não pode excluir essa unidade pois ela já está vinculado à um documento ou possui máquinas!</b-alert>
    </div>
  </div>
</template>
<script>
import axios from "@/api/axios";
import Multiselect from 'vue-multiselect';

const PER_PAGE_OPTIONS = [10, 20, 50, 100];
const PER_PAGE_STORAGE_KEY = 'companies_per_page';

function loadStoredPerPage() {
  const stored = Number(localStorage.getItem(PER_PAGE_STORAGE_KEY));
  return PER_PAGE_OPTIONS.includes(stored) ? stored : PER_PAGE_OPTIONS[0];
}

export default {
  name: "ListCompanies",
  components: {
    Multiselect
  },
  data() {
    return {
      perPage: loadStoredPerPage(),
      perPageOptions: PER_PAGE_OPTIONS,
      currentPage: 1,
      rows: 4,
      allCompanies: [], // base inteira, carregada uma vez (endpoint cacheado no backend)
      filteredCompanies: [],
      companyToDelete: "",
      showAlertDeleteCompany: false,
      dismissSecs: 5,
      dismissCountDown: 0,
      search: "",
      selectedFilter: { text: 'Todas as empresas', value: 'all' },
      filterOptions: [{ text: 'Todas as empresas', value: 'all' }],
      items: [
        {
          text: "Unidades",
          active: true
        }
      ],
      fields: [
        { key: "corporate_name", label: "Nome" },
        { key: "cnpj", label: "CNPJ" },
        { key: "phone", label: "Telefone" },
        { key: "site", label: "Site" },
        { key: "corporate_group.name", label: "Empresa" },
        { key: "id", label: "Ações" }
      ],
      role: localStorage.getItem("auth.role"),
      email: localStorage.getItem("auth.email")
    };
  },
  computed: {
    paginatedCompanies() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredCompanies.slice(start, start + this.perPage);
    }
  },
  async mounted() {
    if (this.role == "admin") {
      this.items.push({
        text: "Nova",
        href: "#/admin/Companies/Create"
      });
    }

    await this.loadAllCompanies();
  },
  methods: {
    // Único lugar que decide se uma empresa bate com o termo buscado — usado tanto
    // pra filtrar a tabela quanto pra montar as sugestões do combo, pra não correr
    // o risco de as duas coisas responderem campos diferentes (foi exatamente esse
    // descompasso, entre filtro do front e busca do backend, que gerou o card #360).
    matchesSearch(company, term) {
      const query = term.toLowerCase();
      const name = company.corporate_name?.toLowerCase() ?? '';
      const cnpj = company.cnpj?.toLowerCase() ?? '';
      const group = company.corporate_group?.name?.toLowerCase() ?? '';
      return name.includes(query) || cnpj.includes(query) || group.includes(query);
    },

    // Carrega a base inteira de uma vez só (endpoint cacheado no backend, ver #351),
    // pra o combo já nascer com sugestões de todas as empresas, não só da página atual.
    async loadAllCompanies() {
      const loader = this.$loading.show();
      try {
        // Endpoint enxuto (só o que essa tela usa), não a listagem completa: essa
        // carregava o grupo "cru", que traz a lista de empresas do próprio grupo
        // aninhada dentro dele mesmo — pesado e redundante pra base grande.
        const response = await axios.get(
          `${process.env.VUE_APP_API_HOST}/api/admin/companies/lean`
        );
        this.allCompanies = response.data.data;
        this.applyFilter();
      } catch (error) {
        console.error('Erro ao carregar unidades:', error);
      } finally {
        loader.hide();
      }
    },

    applyFilter() {
      this.filteredCompanies = this.search
        ? this.allCompanies.filter(company => this.matchesSearch(company, this.search))
        : this.allCompanies;
      this.rows = this.filteredCompanies.length;

      this.filterOptions = [
        { text: 'Todas as empresas', value: 'all' },
        ...this.filteredCompanies.map(c => ({
          text: `${c.corporate_name} (${c.cnpj || ''}${c.corporate_group?.name ? ' - ' + c.corporate_group.name : ''})`,
          value: c.id
        }))
      ];
    },

    // Chamado a cada tecla digitada no combo. Como a base inteira já está carregada,
    // o filtro é instantâneo, sem ida ao servidor.
    onSearchChange(term) {
      if (!term) {
        // O vue-multiselect dispara search-change com string vazia ao perder o foco
        // (limpeza interna do próprio campo) — não é o usuário pedindo pra ver todas
        // de novo. Ignora; pra isso existe a opção "Todas as empresas" no combo.
        return;
      }

      this.search = term;
      this.currentPage = 1;
      this.applyFilter();
    },

    onPerPageChange(value) {
      localStorage.setItem(PER_PAGE_STORAGE_KEY, value);
      this.currentPage = 1;
    },

    onFiltered(filteredItems) {
      this.rows = filteredItems.length;
      this.currentPage = 1;
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown;
    },
    openModal: function(modal, companyId) {
      if (this.canDelete(companyId)) {
        this.$refs[modal].show();
        this.companyToDelete = companyId;
      } else {
        this.showAlertDeleteCompany = this.dismissSecs;
      }
    },
    removeCompany() {
      this.showAlertDeleteCompany = false;
      let promise = axios.delete(
        `${process.env.VUE_APP_API_HOST}/api/admin/companies/${this.companyToDelete}`
      );
      promise.then(() => {
        this.allCompanies = this.allCompanies.filter(el => el.id != this.companyToDelete);
        this.applyFilter();
      });
    },
    canDelete(companyId) {
      let canDelete = this.allCompanies.find(el => el.id == companyId).canDelete;
      return canDelete;
    }
  },
  watch: {
    selectedFilter: {
      handler(newValue) {
        if (!newValue || newValue.value === 'all') {
          // Voltou pra "Todas as empresas": limpa a busca e mostra tudo de novo.
          this.search = "";
          this.currentPage = 1;
          this.applyFilter();
          return;
        }

        // Escolheu uma sugestão específica: mostra só essa empresa.
        const picked = this.allCompanies.find(company => company.id === newValue.value);
        if (picked) {
          this.filteredCompanies = [picked];
          this.rows = 1;
          this.currentPage = 1;
        }
      },
      deep: true
    }
  }
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style>
.pencil {
    color: #007bff;
    background-color: #ffffff;
}
.pencil:hover {
    color: #007bff!important; 
    background-color: #ffffff;
}
.btn-secondary {
    border: none !important; 
    box-shadow: none !important; 
}
.btn-secondary:hover {
    color: #ffffff; 
    box-shadow: none !important; 
}
.trash {
  background-color: #ffffff!important;
}
</style>
